import {
  Registry,
  type GeneratedType,
  type OfflineSigner,
} from '@cosmjs/proto-signing';
import {AminoTypes, SigningStargateClient} from '@cosmjs/stargate';
import {
  cosmosAminoConverters,
  cosmosProtoRegistry,
  cosmwasmAminoConverters,
  cosmwasmProtoRegistry,
  elysAminoConverters,
  elysProtoRegistry,
  ibcAminoConverters,
  ibcProtoRegistry,
} from '@elys-network/elys-telescope';

import {COSMOS_CHAINS} from 'app/chains/cosmos/cosmos.assets';
import {calculateGasPrice, formatAddress} from 'app/utils/cosmos.utils';

import {COSMOS_HUB_CHAIN, ELYS_CHAIN} from 'chains/chains.constants';
import {CosmosChainId, CosmosChainInfo} from 'chains/cosmos/cosmos.types';

import {
  CosmosKitWalletType,
  WalletProvider,
  Web3AuthLoginProvider,
} from '../../../types/providers';

export class BaseAuthProvider {
  type: WalletProvider;

  walletType: CosmosKitWalletType = null!;

  loginProvider: Web3AuthLoginProvider = null!;

  connected = false;

  elysAddress: string = null!;

  // https://docs.keplr.app/api/cosmjs.html
  // Amino is preferred to support Ledger Nano Cosmos App.
  offlineSigner: OfflineSigner = null!;

  elysChain: CosmosChainInfo;

  cosmosChain: CosmosChainInfo;

  get cosmosAddress() {
    return formatAddress(this.cosmosChain.chainId, this.elysAddress);
  }

  constructor(type: WalletProvider) {
    this.type = type;

    this.elysChain = ELYS_CHAIN;
    this.cosmosChain = COSMOS_HUB_CHAIN;
  }

  async init() {}

  async refreshAccount() {
    const addresses = await this.offlineSigner.getAccounts();
    this.elysAddress = addresses[0].address;
  }

  async createSigningStargateClient(
    chainId: CosmosChainId,
    offlineSigner?: OfflineSigner,
  ) {
    if (!offlineSigner) {
      throw new Error('Offline signer is required');
    }

    const account = (await offlineSigner.getAccounts())[0];

    if (!account) {
      throw new Error(`No account available for chain ${chainId}`);
    }

    const chain = COSMOS_CHAINS[chainId];

    const protoRegistry: ReadonlyArray<[string, GeneratedType]> = [
      ...cosmosProtoRegistry,
      ...cosmwasmProtoRegistry,
      ...ibcProtoRegistry,
      ...elysProtoRegistry,
    ];

    const aminoConverters = {
      ...cosmosAminoConverters,
      ...cosmwasmAminoConverters,
      ...ibcAminoConverters,
      ...elysAminoConverters,
    };

    const registry = new Registry(protoRegistry);
    const aminoTypes = new AminoTypes(aminoConverters);

    return SigningStargateClient.connectWithSigner(
      chain.rpcURL,
      offlineSigner,
      {
        registry,
        aminoTypes,
        gasPrice: calculateGasPrice(chainId),
      },
    );
  }

  disconnect() {
    this.connected = false;

    this.offlineSigner = null!;
  }
}
